import { useState } from 'react';
import JSZip from 'jszip';

export interface FaviconOptions {
  sizes: number[];
  addPadding: boolean;
  paddingPercent: number;
  backgroundColor?: string;
  roundCorners: boolean;
  cornerRadius: number;
}

interface GeneratedIcon {
  size: number;
  dataUrl: string;
  filename: string;
}

export const ICON_PRESETS = {
  favicon: [16, 32, 48],
  ios: [120, 152, 167, 180],
  android: [36, 48, 72, 96, 144, 192, 512],
  all: [16, 32, 48, 72, 96, 120, 144, 152, 167, 180, 192, 512],
};

export const useFaviconGenerator = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [generatedIcons, setGeneratedIcons] = useState<GeneratedIcon[]>([]);

  const generateIcon = async (
    imageUrl: string,
    size: number,
    options: FaviconOptions
  ): Promise<GeneratedIcon> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d')!;

          canvas.width = size;
          canvas.height = size;

          // Calculate padding
          const padding = options.addPadding ? (size * options.paddingPercent) / 100 : 0;
          const imageSize = size - padding * 2;

          // Fill background
          if (options.backgroundColor) {
            ctx.fillStyle = options.backgroundColor;
            
            if (options.roundCorners) {
              // Draw rounded rectangle for background
              const radius = (options.cornerRadius / 100) * size;
              ctx.beginPath();
              ctx.moveTo(radius, 0);
              ctx.lineTo(size - radius, 0);
              ctx.quadraticCurveTo(size, 0, size, radius);
              ctx.lineTo(size, size - radius);
              ctx.quadraticCurveTo(size, size, size - radius, size);
              ctx.lineTo(radius, size);
              ctx.quadraticCurveTo(0, size, 0, size - radius);
              ctx.lineTo(0, radius);
              ctx.quadraticCurveTo(0, 0, radius, 0);
              ctx.closePath();
              ctx.fill();
            } else {
              ctx.fillRect(0, 0, size, size);
            }
          }

          // Apply clipping for rounded corners on image
          if (options.roundCorners) {
            const radius = (options.cornerRadius / 100) * imageSize;
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(padding + radius, padding);
            ctx.lineTo(padding + imageSize - radius, padding);
            ctx.quadraticCurveTo(padding + imageSize, padding, padding + imageSize, padding + radius);
            ctx.lineTo(padding + imageSize, padding + imageSize - radius);
            ctx.quadraticCurveTo(padding + imageSize, padding + imageSize, padding + imageSize - radius, padding + imageSize);
            ctx.lineTo(padding + radius, padding + imageSize);
            ctx.quadraticCurveTo(padding, padding + imageSize, padding, padding + imageSize - radius);
            ctx.lineTo(padding, padding + radius);
            ctx.quadraticCurveTo(padding, padding, padding + radius, padding);
            ctx.closePath();
            ctx.clip();
          }

          // Draw image
          ctx.drawImage(img, padding, padding, imageSize, imageSize);

          if (options.roundCorners) {
            ctx.restore();
          }

          const dataUrl = canvas.toDataURL('image/png');
          const filename = `icon-${size}x${size}.png`;

          resolve({ size, dataUrl, filename });
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = imageUrl;
    });
  };

  const generateIcons = async (
    imageUrl: string,
    options: FaviconOptions
  ) => {
    setIsProcessing(true);
    const icons: GeneratedIcon[] = [];

    for (const size of options.sizes) {
      try {
        const icon = await generateIcon(imageUrl, size, options);
        icons.push(icon);
      } catch (error) {
        console.error(`Failed to generate ${size}x${size} icon:`, error);
      }
    }

    setGeneratedIcons(icons);
    setIsProcessing(false);
    return icons;
  };

  const downloadIcon = (icon: GeneratedIcon) => {
    const link = document.createElement('a');
    link.href = icon.dataUrl;
    link.download = icon.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAsZip = async (icons: GeneratedIcon[], zipName: string = 'icons') => {
    const zip = new JSZip();

    icons.forEach((icon) => {
      // Remove data URL prefix
      const base64Data = icon.dataUrl.split(',')[1];
      zip.file(icon.filename, base64Data, { base64: true });
    });

    const blob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${zipName}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const downloadAsIco = async (icons: GeneratedIcon[]) => {
    // For ICO generation, we'll just download the smallest sizes as individual PNGs
    // Full ICO encoding would require additional library
    const faviconSizes = icons.filter(icon => [16, 32, 48].includes(icon.size));
    
    if (faviconSizes.length > 0) {
      // Download the 32x32 as favicon.ico
      const icon32 = faviconSizes.find(i => i.size === 32) || faviconSizes[0];
      const link = document.createElement('a');
      link.href = icon32.dataUrl;
      link.download = 'favicon.ico';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return {
    isProcessing,
    generatedIcons,
    generateIcons,
    downloadIcon,
    downloadAsZip,
    downloadAsIco,
    ICON_PRESETS,
  };
};
